import { create } from 'zustand';
import { gamesService } from '../services/gamesService';
import type { Game, GameFilters, GameSection, Team } from '../types/game';

interface GamesState {
  // Data
  games: Game[];
  sections: GameSection[];
  currentGame: Game | null;
  privateGames: Game[];
  unpublishedGames: Game[];
  userTeams: Team[];
  teamGames: { [teamId: string]: Game[] };
  
  // Loading states
  isLoadingCatalog: boolean;
  isLoadingGame: boolean;
  
  // Error states
  catalogError: string | null;
  gameError: string | null;
  
  // Cache management
  lastFetchTime: number;
  hasLoadedCatalog: boolean;
  
  // Actions
  fetchCatalog: (filters?: GameFilters, force?: boolean) => Promise<void>;
  fetchGameById: (gameId: string) => Promise<Game | null>;
  searchGames: (query: string) => Promise<Game[]>;
  clearCurrentGame: () => void;
  clearErrors: () => void;
  reset: () => void;
  
  // Selectors (derived data)
  getFeaturedGames: (limit?: number) => Game[];
  getTrendingGames: (limit?: number) => Game[];
  getRecentGames: (limit?: number) => Game[];
  getGamesByCategory: (category: string) => Game[];
  getSimilarGames: (gameId: string, limit?: number) => Game[];
  getSections: () => GameSection[];
  getMyGames: () => Game[];
  hasMyGames: () => boolean;
}

// Cache duration: 5 minutes
const CACHE_DURATION = 5 * 60 * 1000;

export const useGamesStore = create<GamesState>((set, get) => ({
  // Initial state
  games: [],
  sections: [],
  currentGame: null,
  privateGames: [],
  unpublishedGames: [],
  userTeams: [],
  teamGames: {},
  isLoadingCatalog: false,
  isLoadingGame: false,
  catalogError: null,
  gameError: null,
  lastFetchTime: 0,
  hasLoadedCatalog: false,
  
  // Actions
  fetchCatalog: async (filters?: GameFilters, force?: boolean) => {
    const { isLoadingCatalog, hasLoadedCatalog, lastFetchTime } = get();
    const now = Date.now();
    
    // Prevent duplicate fetches
    if (isLoadingCatalog) {
      return;
    }
    
    // Use cache if available and not forced
    if (!force && hasLoadedCatalog && (now - lastFetchTime) < CACHE_DURATION) {
      return;
    }
    
    set({ isLoadingCatalog: true, catalogError: null });
    
    try {
      const result = await gamesService.getPersonalizedCatalog(filters, 1, 100);
      
      set({
        games: result.games,
        sections: result.sections || [],
        privateGames: result.privateGames || [],
        unpublishedGames: result.unpublishedGames || [],
        userTeams: result.userTeams || [],
        teamGames: result.teamGames || {},
        isLoadingCatalog: false,
        hasLoadedCatalog: true,
        lastFetchTime: now,
      });
    } catch (error: any) {
      console.error('[GamesStore] Failed to fetch catalog:', error);
      set({
        isLoadingCatalog: false,
        catalogError: error.message || 'Failed to load games',
      });
    }
  },
  
  fetchGameById: async (gameId: string) => {
    const { games, isLoadingGame } = get();
    
    // Check cache first
    const cachedGame = games.find(g => g.id === gameId);
    if (cachedGame) {
      set({ currentGame: cachedGame });
      return cachedGame;
    }
    
    if (isLoadingGame) {
      return null;
    }
    
    set({ isLoadingGame: true, gameError: null });
    
    try {
      const game = await gamesService.getGameById(gameId);
      
      set({
        currentGame: game,
        isLoadingGame: false,
      });
      
      return game;
    } catch (error: any) {
      console.error('[GamesStore] Failed to fetch game:', error);
      set({
        isLoadingGame: false,
        gameError: error.message || 'Failed to load game',
      });
      return null;
    }
  },
  
  searchGames: async (query: string) => {
    try {
      const results = await gamesService.searchGames(query);
      return results;
    } catch (error) {
      console.error('[GamesStore] Search failed:', error);
      return [];
    }
  },
  
  clearCurrentGame: () => {
    set({ currentGame: null, gameError: null });
  },
  
  clearErrors: () => {
    set({ catalogError: null, gameError: null });
  },
  
  reset: () => {
    set({
      games: [],
      sections: [],
      currentGame: null,
      privateGames: [],
      unpublishedGames: [],
      userTeams: [],
      teamGames: {},
      isLoadingCatalog: false,
      isLoadingGame: false,
      catalogError: null,
      gameError: null,
      lastFetchTime: 0,
      hasLoadedCatalog: false,
    });
  },
  
  // Selectors
  getFeaturedGames: (limit = 4) => {
    const { games, sections } = get();
    
    // Use server sections if available
    const featuredSection = sections.find(s => s.type === 'featured');
    if (featuredSection && featuredSection.games.length > 0) {
      return featuredSection.games.slice(0, limit);
    }
    
    // Fallback to client-side sorting by engagement score
    return [...games]
      .sort((a, b) => {
        const scoreA = (a.total_plays || 0) + (a.total_likes || 0) * 10;
        const scoreB = (b.total_plays || 0) + (b.total_likes || 0) * 10;
        return scoreB - scoreA;
      })
      .slice(0, limit);
  },
  
  getTrendingGames: (limit = 4) => {
    const { games, sections } = get();
    
    // Use server sections if available
    const trendingSection = sections.find(s => s.type === 'trending');
    if (trendingSection && trendingSection.games.length > 0) {
      return trendingSection.games.slice(0, limit);
    }
    
    // Fallback to client-side sorting
    return [...games]
      .sort((a, b) => {
        const scoreA = (a.total_plays || 0) + (a.total_likes || 0) * 10;
        const scoreB = (b.total_plays || 0) + (b.total_likes || 0) * 10;
        return scoreB - scoreA;
      })
      .slice(0, limit);
  },
  
  getRecentGames: (limit = 4) => {
    const { games, sections } = get();
    
    // Use server sections if available
    const newSection = sections.find(s => s.type === 'new');
    if (newSection && newSection.games.length > 0) {
      return newSection.games.slice(0, limit);
    }
    
    // Fallback to client-side sorting
    return [...games]
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
      .slice(0, limit);
  },
  
  getGamesByCategory: (category: string) => {
    const { games } = get();
    if (category === 'All') {
      return games;
    }
    return games.filter(g => g.category?.toLowerCase() === category.toLowerCase());
  },
  
  getSimilarGames: (gameId: string, limit = 4) => {
    const { games } = get();
    const currentGame = games.find(g => g.id === gameId);
    
    if (!currentGame) {
      return [];
    }
    
    // Find games in the same category, excluding current
    return games
      .filter(g => g.id !== gameId && g.category === currentGame.category)
      .slice(0, limit);
  },
  
  getSections: () => {
    const { sections } = get();
    return sections;
  },
  
  getMyGames: () => {
    const { teamGames } = get();
    
    // Flatten all team games into a single array
    const allTeamGames: Game[] = [];
    Object.values(teamGames).forEach(games => {
      allTeamGames.push(...games);
    });
    
    // Remove duplicates (in case a game appears in multiple teams)
    const uniqueGames = allTeamGames.reduce((acc, game) => {
      if (!acc.find(g => g.id === game.id)) {
        acc.push(game);
      }
      return acc;
    }, [] as Game[]);
    
    // Sort by updated date (most recent first)
    return uniqueGames.sort((a, b) => 
      new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
    );
  },
  
  hasMyGames: () => {
    const { teamGames } = get();
    return Object.values(teamGames).some(games => games.length > 0);
  },
}));

